import { useEffect, useRef } from 'react';
import Quill from 'quill';
import ImageUploader from 'quill-image-uploader';
import 'quill/dist/quill.snow.css';
import 'quill-image-uploader/dist/quill.imageUploader.min.css';
import { FormControl, FormLabel } from '@chakra-ui/react';
import EventsService from '../../../services/events/eventsService';
import { BASE_URL } from '../../../config';
import Delta from 'quill-delta';

Quill.register('modules/imageUploader', ImageUploader);

const QuillEditor = ({ label, value, onChange, isDisabled }) => {
    const editorRef = useRef(null);
    const quillRef = useRef(null);
    const settingFromOutside = useRef(false);
    const changingFromInside = useRef(false);
    const textChangeHandlerRef = useRef(null);

    useEffect(() => {
        if (!editorRef.current || quillRef.current) return;

        const quill = new Quill(editorRef.current, {
            theme: 'snow',
            readOnly: !!isDisabled,
            modules: {
                toolbar: [
                    ['bold', 'italic', 'underline'],
                    ['link', 'image'],
                    [{ list: 'ordered' }, { list: 'bullet' }],
                    ['clean'],
                ],
                imageUploader: {
                    upload: async (file) => {
                        const image = await EventsService.uploadCoverImage(file);
                        return `${BASE_URL}/${image.data}`;
                    },
                },
                clipboard: {
                    matchers: [
                        ['img', (node/*: HTMLImageElement*/, _delta) => {
                            const raw = node.getAttribute('src') || '';
                            const isBase64 = /^\s*data:/i.test(raw);
                            if (isBase64) return new Delta(); // wytnij base64 całkowicie

                            const fixed = raw.replace(/\\/g, '/'); // normalizuj backslash'e
                            // zwróć nowy Delta z obrazkiem
                            return new Delta().insert({ image: fixed });
                        }]
                    ]
                }
            },
        });

        quillRef.current = quill;

        // Początkowa wartość (jeśli przyszła przed init)
        settingFromOutside.current = true;
        try {
            applyIncomingValue(quill, value);
        } finally {
            settingFromOutside.current = false;
        }
        const handler = () => {
            if (settingFromOutside.current) return;
            changingFromInside.current = true;
            onChange?.(quill.root.innerHTML);
        };
        textChangeHandlerRef.current = handler;
        quill.on('text-change', handler);

        return () => {
            if (quill && textChangeHandlerRef.current) {
                quill.off('text-change', textChangeHandlerRef.current);
            }
            quillRef.current = null;
        };
    }, [editorRef]);

    useEffect(() => {
        const quill = quillRef.current;
        if (!quill) return;

        if (changingFromInside.current) {
            changingFromInside.current = false;
            return;
        }

        const current = quill.root.innerHTML || '';
        const incomingStr = normalizeIncomingToHtmlString(value);

        if (incomingStr === null || incomingStr !== current) {
            settingFromOutside.current = true;
            try {
                applyIncomingValue(quill, value);
            } finally {
                settingFromOutside.current = false;
            }
        }
    }, [value]);

    useEffect(() => {
        const quill = quillRef.current;
        if (!quill) return;
        // readOnly w API Quilla = odwrotność enable()
        quill.enable(!isDisabled);
    }, [isDisabled]);

    return (
        <FormControl>
            {label && <FormLabel>{label}</FormLabel>}
            <div
                ref={editorRef}
                style={{
                    height: '100%',
                    minHeight: '35em',
                    whiteSpace: 'pre-wrap',
                }}
            />
        </FormControl>
    );
};

export default QuillEditor;

function applyIncomingValue(quill, incoming) {
    if (incoming == null) {
        quill.setContents(new Delta());
        return;
    }
    if (typeof incoming === 'object' && incoming.ops) {
        const norm = new Delta(
            incoming.ops.map(op =>
                op.insert && op.insert.image
                    ? { ...op, insert: { image: String(op.insert.image).replace(/\\/g, '/') } }
                    : op
            )
        );
        quill.setContents(norm);
        return;
    }

    if (typeof incoming === 'string') {
        const trimmed = incoming.trim();

        if (trimmed.startsWith('{')) {
            try {
                const maybeDelta = JSON.parse(trimmed);
                if (maybeDelta && maybeDelta.ops) {
                    const norm = new Delta(
                        maybeDelta.ops.map(op =>
                            op.insert && op.insert.image
                                ? { ...op, insert: { image: String(op.insert.image).replace(/\\/g, '/') } }
                                : op
                        )
                    );
                    quill.setContents(norm);
                    return;
                }
            } catch {
            }
        }
        const normalizedHtml = trimmed.replace(
            /(<img[^>]*\ssrc\s*=\s*")([^"]*)(")/gi,
            (_m, p1, url, p3) => `${p1}${String(url).replace(/\\/g, '/')}${p3}`
        );

        quill.clipboard.dangerouslyPasteHTML(normalizedHtml || '');
        return;
    }
    quill.setContents(new Delta());
}

function normalizeIncomingToHtmlString(incoming) {
    if (incoming == null) return '';
    if (typeof incoming === 'string') return incoming;
    if (typeof incoming === 'object' && incoming.ops) return null; // zmuszamy do apply
    return String(incoming ?? '');
}
